import { cn } from '@/lib/cn';
import { getTeamTheme } from '@mondy/shared';
import { FlagPattern } from './FlagPattern';

type FlagSize = 'xs' | 'sm' | 'md' | 'lg';

interface TeamFlagProps {
  /** FIFA code — drives the abstract per-country styling. */
  code?: string | null;
  /** Team display name (fallback for 1-letter initial). */
  name?: string;
  /**
   * Real flag image (from flagcdn.com). Wins over the abstract SVG
   * pattern when present — the per-team patterns in `team-theme.ts`
   * are stylized approximations and several (BRA, RSA, USA, AUS, …)
   * read as the wrong country at a glance.
   */
  url?: string | null;
  size?: FlagSize;
  /** Hide the letter code inside the tile (used in dense match-card rows). */
  mute?: boolean;
  className?: string;
}

const SIZE_CLASS: Record<FlagSize, string> = {
  xs: 'xs',
  sm: 'sm',
  md: '',
  lg: 'big-flag',
};

export function TeamFlag({
  code,
  name,
  url,
  size = 'sm',
  mute = false,
  className,
}: TeamFlagProps) {
  const label = (code ?? name?.charAt(0) ?? '').toUpperCase();
  const sz = SIZE_CLASS[size];

  if (url) {
    return (
      <span
        className={cn('flag', 'flag-image', sz, mute && 'mute', className)}
        style={{
          backgroundImage: `url(${url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'transparent',
        }}
        aria-hidden
      >
        {label}
      </span>
    );
  }

  const theme = code ? getTeamTheme(code) : null;
  if (theme) {
    return (
      <span
        className={cn('flag', 'flag-pattern', sz, mute && 'mute', className)}
        data-code={code ?? undefined}
        aria-hidden
      >
        <FlagPattern spec={theme.pattern} />
      </span>
    );
  }

  return (
    <span
      className={cn('flag', sz, mute && 'mute', className)}
      data-code={code ?? undefined}
      aria-hidden
    >
      {label}
    </span>
  );
}
